import { NotificationSeverity } from '@/components/notifications/Notification';
import { MAX_LABELS_PER_PRINT } from '../constants/routing.constants';
import { useCallback, useRef, useState } from 'react';
import useNotification from '@/hooks/useNotification';
import { getErrorMessage } from '@/utils/errorUtils';
import {
  useInitiateCombinedLabelsPdfMutation,
  useInitiateRouteLabelsPdfMutation,
  useLazyGetCombinedLabelsPdfJobStatusQuery,
  CombinedLabelsJobStatus,
} from '../services';
import type {
  CombinedLabelsFailedShipping,
  RouteLabelsJobInitRequest,
} from '../services';

const POLLING_INTERVAL_MS = 3000;
const MIN_POLLING_DURATION_MS = 2 * 60 * 1000;
const POLLING_MS_PER_LABEL = 1500;

const maxPollingDurationMs = (labelCount: number): number =>
  Math.max(MIN_POLLING_DURATION_MS, labelCount * POLLING_MS_PER_LABEL);

export type BatchLabelJobMode = 'print' | 'download';

export type BatchLabelJobPhase = 'running' | 'completed' | 'errored';

export interface BatchLabelJobState {
  mode: BatchLabelJobMode;
  phase: BatchLabelJobPhase;
  /** Cantidad de etiquetas realmente enviadas al job (ya recortada al máximo). */
  requestedCount: number;
  jobId: string | null;
  downloadUrl: string | null;
  fileName: string | null;
  errorMessage: string | null;
  failedShippings: CombinedLabelsFailedShipping[];
  startedAtMs: number;
}

interface StartedLabelsJob {
  jobId: string;
  labelCount: number;
}

/**
 * Genera un PDF combinado de etiquetas como job asíncrono y expone su estado
 * para que la UI lo muestre con la estética del sistema.
 *
 * El PDF resultante vive en S3 (cross-origin), así que no podemos forzar una
 * descarga real vía blob: el usuario lo abre y guarda desde el visor nativo.
 * La apertura queda a cargo de un botón del propio panel de progreso —un gesto
 * explícito del usuario— en vez de un `window.open` diferido, que los
 * bloqueadores de popups rechazan por ejecutarse fuera del click original.
 */
export const useBatchLabelPrint = () => {
  const [job, setJob] = useState<BatchLabelJobState | null>(null);
  const [initiateCombinedLabelsPdf] = useInitiateCombinedLabelsPdfMutation();
  const [initiateRouteLabelsPdf] = useInitiateRouteLabelsPdfMutation();
  const [getJobStatus] = useLazyGetCombinedLabelsPdfJobStatusQuery();
  const { showNotification } = useNotification();

  // Evita que un job abandonado (el usuario cerró el panel y lanzó otro) siga
  // escribiendo sobre el estado del job vigente.
  const activeJobIdRef = useRef<string | null>(null);

  const isRunning = job?.phase === 'running';
  const isPrinting = isRunning && job?.mode === 'print';
  const isDownloading = isRunning && job?.mode === 'download';

  const pollJobStatus = useCallback(
    async (jobId: string, startedAtMs: number, maxDurationMs: number) => {
      if (activeJobIdRef.current !== jobId) return;

      try {
        if (Date.now() - startedAtMs > maxDurationMs) {
          throw new Error(
            'La generación de etiquetas tardó demasiado. Podés seguirla desde Mis archivos.'
          );
        }

        const { data: statusJob, error } = await getJobStatus(jobId);

        if (activeJobIdRef.current !== jobId) return;

        if (error || !statusJob) {
          throw new Error('Error al consultar el estado de las etiquetas');
        }

        if (
          statusJob.status === CombinedLabelsJobStatus.COMPLETED &&
          statusJob.downloadUrl
        ) {
          const failedShippings =
            statusJob.filterPayload?.result?.failedShippings ?? [];
          setJob((current) =>
            current && current.jobId === jobId
              ? {
                  ...current,
                  phase: 'completed',
                  downloadUrl: statusJob.downloadUrl ?? null,
                  fileName: statusJob.fileName ?? null,
                  failedShippings,
                }
              : current
          );
          showNotification(
            failedShippings.length > 0
              ? `Etiquetas generadas, pero ${failedShippings.length} no se pudieron generar`
              : 'Etiquetas generadas con éxito',
            failedShippings.length > 0
              ? NotificationSeverity.warning
              : NotificationSeverity.success
          );
          return;
        }

        if (statusJob.status === CombinedLabelsJobStatus.ERRORED) {
          throw new Error(
            statusJob.errorMessage ||
              'Error en el servidor al generar las etiquetas'
          );
        }

        setTimeout(
          () => pollJobStatus(jobId, startedAtMs, maxDurationMs),
          POLLING_INTERVAL_MS
        );
      } catch (err: unknown) {
        if (activeJobIdRef.current !== jobId) return;
        const message = getErrorMessage(err, 'Error al generar las etiquetas');
        setJob((current) =>
          current && current.jobId === jobId
            ? { ...current, phase: 'errored', errorMessage: message }
            : current
        );
        showNotification(message, NotificationSeverity.error);
      }
    },
    [getJobStatus, showNotification]
  );

  const runLabelsJob = useCallback(
    async (
      mode: BatchLabelJobMode,
      expectedCount: number,
      start: () => Promise<StartedLabelsJob>
    ) => {
      if (isRunning) return;

      const startedAtMs = Date.now();

      activeJobIdRef.current = null;
      setJob({
        mode,
        phase: 'running',
        requestedCount: expectedCount,
        jobId: null,
        downloadUrl: null,
        fileName: null,
        errorMessage: null,
        failedShippings: [],
        startedAtMs,
      });

      try {
        const { jobId, labelCount } = await start();
        activeJobIdRef.current = jobId;
        setJob((current) =>
          current && current.startedAtMs === startedAtMs
            ? { ...current, jobId, requestedCount: labelCount }
            : current
        );
        pollJobStatus(jobId, startedAtMs, maxPollingDurationMs(labelCount));
      } catch (err: unknown) {
        const message = getErrorMessage(
          err,
          'No se pudo iniciar la generación de etiquetas'
        );
        setJob((current) =>
          current && current.startedAtMs === startedAtMs
            ? { ...current, phase: 'errored', errorMessage: message }
            : current
        );
        showNotification(message, NotificationSeverity.error);
      }
    },
    [pollJobStatus, showNotification, isRunning]
  );

  const runBatchLabelsJob = useCallback(
    (shippingNumbers: number[], mode: BatchLabelJobMode) => {
      if (shippingNumbers.length === 0) return;
      const numbers = shippingNumbers.slice(0, MAX_LABELS_PER_PRINT);
      return runLabelsJob(mode, numbers.length, async () => {
        const { jobId } = await initiateCombinedLabelsPdf(numbers).unwrap();
        return { jobId, labelCount: numbers.length };
      });
    },
    [initiateCombinedLabelsPdf, runLabelsJob]
  );

  const printRoute = useCallback(
    (request: RouteLabelsJobInitRequest, expectedCount: number) =>
      runLabelsJob('print', expectedCount, async () => {
        const { jobId, shippingCount } =
          await initiateRouteLabelsPdf(request).unwrap();
        return { jobId, labelCount: shippingCount };
      }),
    [initiateRouteLabelsPdf, runLabelsJob]
  );

  const printShippings = useCallback(
    (shippingNumbers: number[]) => runBatchLabelsJob(shippingNumbers, 'print'),
    [runBatchLabelsJob]
  );

  const downloadShippings = useCallback(
    (shippingNumbers: number[]) =>
      runBatchLabelsJob(shippingNumbers, 'download'),
    [runBatchLabelsJob]
  );

  /** Cierra el panel. Un job en curso se abandona: sigue vivo en Mis archivos. */
  const dismissJob = useCallback(() => {
    activeJobIdRef.current = null;
    setJob(null);
  }, []);

  return {
    printShippings,
    isPrinting,
    downloadShippings,
    isDownloading,
    printRoute,
    job,
    dismissJob,
  };
};
